<template>
  <div class="benchmark-details-panel" v-if="isVisible">
    <div class="content px-5 flex flex-col gap-4">
      <div class="px-2" style="margin-top: 100px; margin-left: -15px; padding-left:0;">
        <div class="flex justify-between items-center">
          <h1 class="client-details-welcome-title-consultant px-0">{{ title }}</h1>

          <IciButton id="backToResults" :content="chevronLeft.replace('$label', 'Back to results')"
            @buttonClick="closeDetails" btnStyle="width: 180px;">
          </IciButton>
        </div>

  
      </div>

      <div class="content-container text-left">
        <div v-for="(section, index) in benchmarkData.sections" :key="index" class="section-box" :style="{
          gridColumn: getGridColumn(section),
          gridRow: getGridRow(section)
        }">
          <div class="itemHead col-span-12 bg-[#f3f4f8] px-3 rounded-xl flex items-center justify-between min-h-14">
            <h3 class="font-bold">{{ section.title }}</h3>
          </div>
          <div class="section-content">
            <p v-for="(paragraph, pIndex) in section.content" :key="'p-' + pIndex">
              {{ paragraph }}
            </p>

            <div v-if="section.lists">
              <div v-for="(list, lIndex) in section.lists" :key="'list-' + lIndex" class="list-container">
                <h3>{{ list.title }}</h3>
                <ul>
                  <li v-for="(item, iIndex) in list.items" :key="'item-' + iIndex">{{ item }}</li>
                </ul>
              </div>
            </div>

            <p v-if="section.additionalContent" v-for="(paragraph, aIndex) in section.additionalContent"
              :key="'a-' + aIndex">
              {{ paragraph }}
            </p>

            <div v-if="section.tables">
              <div v-for="(table, tIndex) in section.tables" :key="'table-' + tIndex" class="table-container">
                <h3>{{ table.title }}</h3>
                <table>
                  <thead>
                    <tr>
                      <th v-for="(header, hIndex) in table.headers" :key="'h-' + hIndex">{{ header }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rIndex) in table.rows" :key="'row-' + rIndex">
                      <td v-for="(cell, cIndex) in row" :key="'cell-' + cIndex" v-html="cell"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import IciButton from '../IciButton.vue';

export default {
  name: 'BenchmarkDetailsPanel',
  components: { IciButton },
  data() {
    return {
      chevronLeft: "<i class='pi pi-chevron-left' style='font-size: 1rem'></i>&nbsp;&nbsp;<label>$label</label>",
      title: "Understanding the Results"
    }
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const benchmarkData = ref({
      title: "",
      sections: []
    });

    onMounted(async () => {
      try {
        const response = await axios.get('/data/benchmark-details.json');
        benchmarkData.value = response.data;
      } catch (error) {
        console.error('Failed to load benchmark details:', error);
      }
    });

    const closeDetails = () => {
      emit('close');
    };

    // Helper methods for better layout control
    const getGridColumn = (section) => {
      // You can customize this based on section content or title
      // Examples: larger sections could span both columns
      if (section.title === "Sources of Benchmarking Criteria") {
        return "span 1"; // Takes 1 column
      }
      return "auto"; // Default auto placement
    };

    const getGridRow = (section) => {
      // You can customize which row each section appears in
      return "auto"; // Default auto placement
    };

    return {
      benchmarkData,
      closeDetails,
      getGridColumn,
      getGridRow
    };
  }
}
</script>

<style scoped>
.benchmark-details-panel {
  position: fixed;
  top: 0;
  left: 110px;
  right: 0;
  bottom: 0;
  background-color: #fff;
  z-index: 50;
  overflow-y: auto;
  padding: 0;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.content-container {
  column-count: 2;
  /* Use CSS columns instead of grid */
  column-gap: 40px;
  /* margin: 0 120px 50px 120px; */
}

.section-box {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: inline-block;
  /* Important for column layout */
  width: 100%;
  margin-bottom: 20px;
  break-inside: avoid;
  /* Prevents a section from breaking across columns */
}

h2 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  padding: 15px 20px;
  background-color: #f5f5f5;
}

.section-content {
  padding: 20px;
  flex-grow: 1;
}

p {
  margin-bottom: 15px;
  line-height: 1.5;
  color: #333;
  font-size: 14px;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 10px 0;
}

ul {
  margin: 10px 0 15px 0;
  padding-left: 20px;
  list-style-type: disc;
}

li {
  margin-bottom: 5px;
  line-height: 1.5;
  color: #333;
  font-size: 14px;
  position: relative;
  padding-left: 5px;
}

li::marker {
  color: #000;
  font-size: 1.2em;
}

.list-container {
  margin-bottom: 15px;
}

.table-container {
  margin-top: 15px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

th,
td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

th {
  background-color: #f5f5f5;
  font-weight: bold;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

@media (max-width: 1200px) {
  .content-container {
    column-count: 1;
    margin: 0 40px 30px 40px;
  }
}
</style>